import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Home as HomeIcon,
  AttachMoney as PriceIcon,
  Schedule as TimeIcon,
  CheckCircle as CheckIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import { CMAResult } from '../../../types/property';

interface CMATabProps {
  data: CMAResult;
  loading?: boolean;
  error?: string;
}

const CMATab: React.FC<CMATabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading comparative market analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No comparative market analysis available
      </Alert>
    );
  }

  const { cmaAnalysis } = data;
  const { subjectProperty, comparables, marketTrends, competitiveAnalysis, marketInsights, recommendations } = cmaAnalysis;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const getTrendIcon = (direction: string) => {
    if (direction === 'appreciating') return <TrendingUpIcon color="success" />;
    if (direction === 'depreciating') return <TrendingDownIcon color="error" />;
    return <AnalyticsIcon />;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Subject Property Valuation */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon color="primary" />
                  Property Valuation
                </Typography>
                <Chip
                  icon={<CheckIcon />}
                  label={`${Math.round(subjectProperty.confidence * 100)}% Confidence`}
                  color={getConfidenceColor(subjectProperty.confidence)}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {formatPrice(subjectProperty.estimatedValue)}
                  </Typography>
                  <Typography variant="subtitle1">
                    Estimated Value
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Value Range
                  </Typography>
                  <Typography variant="body1">
                    {formatPrice(subjectProperty.valueRange.min)} - {formatPrice(subjectProperty.valueRange.max)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Low - High
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Price per Sq Ft
                  </Typography>
                  <Typography variant="h5" color="primary">
                    ${subjectProperty.pricePerSqFt}
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Market Position
                  </Typography>
                  <Chip label={subjectProperty.marketPosition} color="primary" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Market Trends */}
          <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {getTrendIcon(marketTrends.marketDirection)}
                Market Trends
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Average Price</Typography>
                  <Typography variant="h6">{formatPrice(marketTrends.averagePrice)}</Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Median Price</Typography>
                  <Typography variant="h6">{formatPrice(marketTrends.medianPrice)}</Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Avg Days on Market</Typography>
                  <Typography variant="h6">{marketTrends.daysOnMarket.average} days</Typography>
                </Box>
                
                <Box sx={{ flex: '1 1 200px' }}>
                  <Typography variant="body2" color="text.secondary">Inventory Level</Typography>
                  <Chip 
                    label={marketTrends.inventoryLevel} 
                    color={marketTrends.inventoryLevel === 'low' ? 'error' : marketTrends.inventoryLevel === 'high' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Market Direction
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTrendIcon(marketTrends.marketDirection)}
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {marketTrends.marketDirection}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Seasonal Factor ({marketTrends.seasonality.currentSeason})
                </Typography>
                <Typography variant="body1" color={marketTrends.seasonality.seasonalFactor > 1 ? 'success.main' : 'error.main'}>
                  {(marketTrends.seasonality.seasonalFactor * 100).toFixed(0)}% of normal activity
                </Typography>
              </Box>
            </CardContent>
          </Card>
          </Box>

          {/* Competitive Analysis Summary */}
          <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareIcon color="primary" />
                Competitive Analysis
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Recommended Price</Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {formatPrice(competitiveAnalysis.recommendedPrice)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Negotiation Range</Typography>
                <Typography variant="body1">
                  {formatPrice(competitiveAnalysis.negotiationRange.min)} - {formatPrice(competitiveAnalysis.negotiationRange.max)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Pricing Strategy</Typography>
                <Chip label={competitiveAnalysis.pricingStrategy} color="secondary" />
              </Box>

              {competitiveAnalysis.advantages && competitiveAnalysis.advantages.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Key Advantages
                  </Typography>
                  <List dense>
                    {competitiveAnalysis.advantages.slice(0, 3).map((advantage, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={advantage} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
          </Box>
        </Box>

        {/* Comparable Properties Table */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon color="primary" />
                Comparable Properties
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Address</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Price/SqFt</TableCell>
                      <TableCell align="center">Beds/Baths</TableCell>
                      <TableCell align="right">Sq Ft</TableCell>
                      <TableCell align="right">DOM</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparables.map((comp, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {comp.address}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatPrice(comp.price)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            ${comp.pricePerSqFt}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {comp.bedrooms}/{comp.bathrooms}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {comp.squareFootage.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {comp.daysOnMarket}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={comp.status} 
                            color={comp.status === 'sold' ? 'success' : comp.status === 'active' ? 'primary' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Market Insights */}
          <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon color="primary" />
                Market Insights
              </Typography>

              {marketInsights.demandFactors && marketInsights.demandFactors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                    Demand Factors
                  </Typography>
                  <List dense>
                    {marketInsights.demandFactors.map((factor, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <TrendingUpIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={factor} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {marketInsights.supplyFactors && marketInsights.supplyFactors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'info.main' }}>
                    Supply Factors
                  </Typography>
                  <List dense>
                    {marketInsights.supplyFactors.map((factor, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <HomeIcon fontSize="small" color="info" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={factor} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {marketInsights.externalFactors && marketInsights.externalFactors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.main' }}>
                    External Factors
                  </Typography>
                  <List dense>
                    {marketInsights.externalFactors.map((factor, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <AssessmentIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={factor} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
          </Box>

          {/* Recommendations */}
          <Box sx={{ flex: 1 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="primary" />
                Recommendations
              </Typography>

              {recommendations.pricing && recommendations.pricing.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                    Pricing
                  </Typography>
                  <List dense>
                    {recommendations.pricing.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <PriceIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={rec} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {recommendations.marketing && recommendations.marketing.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main' }}>
                    Marketing
                  </Typography>
                  <List dense>
                    {recommendations.marketing.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <TrendingUpIcon fontSize="small" color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={rec} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {recommendations.timing && recommendations.timing.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                    Timing
                  </Typography>
                  <List dense>
                    {recommendations.timing.map((rec, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <TimeIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={rec} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CMATab;